import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import i18n from '../i18n';
import { Menu, Globe, User, LogOut, ChevronDown, Star } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentLang = i18n.language;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const switchLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('zampa_lang', lang);
  };

  const handleLogout = () => {
    logout();
    toast.success(t('auth.logout_success'));
    navigate('/');
  };

  const navLinks = [
    { to: '/',                      label: t('nav.home') || 'Home' },
    { to: '/servizi',               label: t('nav.services') },
    { to: '/shop',                  label: t('nav.shop') },
    { to: '/giardino-delle-stelle', label: t('nav.garden'), star: true },
    { to: '/community',             label: t('nav.community') },
    { to: '/chi-siamo',             label: t('nav.about') },
    { to: '/contatti',              label: t('nav.contact') },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 border-b border-[var(--color-border)] ${
        scrolled
          ? 'shadow-md bg-[var(--color-paper)] backdrop-blur-none'
          : 'bg-[var(--color-paper)]'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-[88px]">

          {/* ── Logo ── */}
          <a href="/" data-testid="logo-link" className="flex items-center group">
            <img
              src="/logo.png"
              alt="Una Zampa in Paradiso"
              className="h-20 w-auto object-contain rounded-xl group-hover:opacity-90 transition-opacity duration-150"
              loading="eager"
            />
          </a>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="main-navigation">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-[13px] font-medium transition-colors duration-150 ${
                    isActive
                      ? 'text-[var(--color-brand)] bg-[var(--color-sage-soft)]'
                      : 'text-[var(--color-ink)] hover:text-[var(--color-brand)] hover:bg-[var(--color-sage-soft)]'
                  }`
                }
              >
                {link.star ? (
                  <span className="flex items-center gap-1">
                    <Star size={12} className="text-[var(--color-terracotta)]" fill="currentColor" />
                    {link.label}
                  </span>
                ) : link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5">

            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 text-[var(--color-muted-text)] h-8 px-2" data-testid="language-switcher">
                  <Globe size={15} />
                  <span className="uppercase text-xs font-bold">{currentLang}</span>
                  <ChevronDown size={11} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--color-paper)] border-[var(--color-border)] rounded-xl">
                <DropdownMenuItem onClick={() => switchLanguage('it')} className={currentLang === 'it' ? 'text-[var(--color-brand)] font-semibold' : ''}>
                  Italiano
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => switchLanguage('en')} className={currentLang === 'en' ? 'text-[var(--color-brand)] font-semibold' : ''}>
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline" size="sm"
                    className="hidden sm:flex gap-1.5 border-[var(--color-border)] text-[var(--color-ink)] hover:bg-[var(--color-sage-soft)] hover:text-[var(--color-brand)] rounded-xl h-8 text-xs"
                    data-testid="user-menu-button"
                  >
                    <div className="w-5 h-5 rounded-full bg-[var(--color-brand)] flex items-center justify-center">
                      <span className="text-white text-[9px] font-bold">{user.first_name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span>{user.first_name}</span>
                    <ChevronDown size={11} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[var(--color-paper)] border-[var(--color-border)] min-w-[180px] rounded-xl">
                  <DropdownMenuItem onClick={() => navigate('/area-clienti')} data-testid="nav-dashboard">
                    <User size={13} className="mr-2" />{t('nav.dashboard')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                      <Star size={13} className="mr-2" />{t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-[var(--color-terracotta)]" data-testid="logout-button">
                    <LogOut size={13} className="mr-2" />{t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                size="sm"
                className="hidden sm:flex bg-[var(--color-brand)] hover:bg-[var(--color-deep)] text-white rounded-xl h-8 text-xs btn-press px-4"
                onClick={() => navigate('/login')}
                data-testid="header-login-button"
              >
                {t('nav.login')}
              </Button>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="lg:hidden h-8 w-8 p-0" data-testid="mobile-menu-open-button">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[var(--color-paper)] border-[var(--color-border)] w-[300px] pt-8" data-testid="mobile-menu-sheet">
                <div className="flex flex-col gap-1.5">
                  {/* Logo nel menu mobile */}
                  <div className="mb-5 px-1">
                    <img
                      src="/logo.png"
                      alt="Una Zampa in Paradiso"
                      className="h-16 w-auto object-contain rounded-xl"
                    />
                  </div>
                  {navLinks.map(link => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${
                          isActive
                            ? 'text-[var(--color-brand)] bg-[var(--color-sage-soft)]'
                            : 'text-[var(--color-ink)] hover:bg-[var(--color-linen)]'
                        }`
                      }
                    >
                      {link.star && <Star size={13} className="text-[var(--color-terracotta)]" fill="currentColor" />}
                      {link.label}
                    </NavLink>
                  ))}
                  <div className="border-t border-[var(--color-border)] mt-3 pt-3 space-y-1">
                    {user ? (
                      <>
                        <button onClick={() => { navigate('/area-clienti'); setMobileOpen(false); }} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm w-full text-left hover:bg-[var(--color-linen)]">
                          <User size={14} />{t('nav.dashboard')}
                        </button>
                        {isAdmin && (
                          <button onClick={() => { navigate('/admin'); setMobileOpen(false); }} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm w-full text-left hover:bg-[var(--color-linen)]">
                            <Star size={14} />{t('nav.admin')}
                          </button>
                        )}
                        <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm w-full text-left text-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-soft)]">
                          <LogOut size={14} />{t('nav.logout')}
                        </button>
                      </>
                    ) : (
                      <>
                        <Button className="w-full bg-[var(--color-brand)] hover:bg-[var(--color-deep)] text-white rounded-xl mb-1" onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                          {t('nav.login')}
                        </Button>
                        <Button variant="outline" className="w-full border-[var(--color-border)] rounded-xl" onClick={() => { navigate('/registrati'); setMobileOpen(false); }}>
                          {t('nav.register')}
                        </Button>
                      </>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => switchLanguage('it')} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${ currentLang === 'it' ? 'bg-[var(--color-brand)] text-white border-[var(--color-brand)]' : 'border-[var(--color-border)] text-[var(--color-muted-text)] hover:border-[var(--color-brand)]' }`}>IT</button>
                      <button onClick={() => switchLanguage('en')} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${ currentLang === 'en' ? 'bg-[var(--color-brand)] text-white border-[var(--color-brand)]' : 'border-[var(--color-border)] text-[var(--color-muted-text)] hover:border-[var(--color-brand)]' }`}>EN</button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
